/**
 * CruiseSection.tsx
 * クルーズ船 — クルーズ船の予約（宮古=平良港・那覇=那覇港）の船・プラン・返却の期限・事前決済を1か所に出す（2026-11-02）。
 *
 * - クルーズ船の予約でなければ何も出さない（判定は lib/cruiseReservation.ts parseCruiseReservation。daylist の 🚢 と同じ）
 * - 「お渡し場所の案内」はスタッフが付け外しする。onChange で下書きの json_data を差し替えるだけで、
 *   確定はパネル本体の保存（ホテル送迎セクション・JsonDataModal と同じ経路。予約保存ルール md/master_md/ref-save-reservation.md）
 * - 料金の決まりの正: booking-platform/md/pricing.md。仕様: md/master_md/feature-sidepanel.md §4c
 */
import React from 'react';
import { SectionProps } from '../types';
import SectionHeader from '../shared/SectionHeader';
import { FieldMarkWrap } from '../conflict/fieldState';
import { getStoreKey } from '../../../config/backend';
import {
  parseCruiseReservation,
  cruisePriceKindLabel,
  cruiseReturnRule,
  applyCruiseHandoverGuided,
} from '../../../lib/cruiseReservation';
import { CRUISE_COLOR } from '../../../utils/planPrefix';

const JSON_FIELDS = ['json_data'] as const;

const BLUE = { border: CRUISE_COLOR, bg: '#eff6ff', text: '#1e3a8a', sub: '#1e40af' };

interface CruiseSectionProps extends SectionProps {
  /** 店舗（テスト用。省略時はこの r-cal の店舗） */
  store?: 'ncar' | 'pit';
}

const yen = (n: number) => `¥${n.toLocaleString('ja-JP')}`;

export default function CruiseSection({ data, onChange, isNew, disabled, store }: CruiseSectionProps) {
  if (isNew) return null;
  const c = parseCruiseReservation(data.json_data, data.comment);
  if (!c) return null;
  const storeKey = store || getStoreKey();
  const port = storeKey === 'pit' ? '平良港' : '那覇港';
  const rule = cruiseReturnRule(c, storeKey, data.start_date, data.end_date);
  const canEdit = !disabled && !!onChange;
  const toggleGuided = (guided: boolean) => onChange?.({ json_data: applyCruiseHandoverGuided(data.json_data, guided) });

  return (
    <>
      <SectionHeader title="クルーズ船" />
      <FieldMarkWrap fields={JSON_FIELDS} chip>
        <div
          data-testid="cruise-box"
          style={{
            border: `1px solid ${BLUE.border}`, borderLeft: `4px solid ${BLUE.border}`, background: BLUE.bg,
            borderRadius: 6, padding: '8px 12px', fontSize: '0.85rem', color: BLUE.text,
            display: 'flex', flexDirection: 'column', gap: 4,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, background: BLUE.border, color: '#fff', borderRadius: 4, padding: '1px 8px', fontSize: '0.78rem' }}>
              🚢 クルーズ船
            </span>
            <span style={{ fontWeight: 600 }}>{port}</span>
          </div>

          {c.ship ? (
            <>
              <Row label="船名" value={c.ship.name} strong />
              <Row label="寄港日" value={c.ship.departDate ? `${c.ship.arriveDate}〜${c.ship.departDate}` : c.ship.arriveDate} />
              <Row label="入港・出港" value={`入港 ${c.ship.arrive}／出港 ${c.ship.departDate ? `${c.ship.departDate} ` : ''}${c.ship.depart}`} />
            </>
          ) : (
            <Row label="船" value={c.shipLabel || '指定なし（日時で予約）'} strong={!!c.shipLabel} />
          )}

          <Row label="プラン" value={cruisePriceKindLabel(c)} strong={!!c.planLabel} />
          {c.planWindow && <Row label="プランの時間" value={c.planWindow} />}
          {c.planPrice != null && <Row label="プラン料金" value={`${yen(c.planPrice)}（CDW・NOC・港の送迎料・給油・充電込み）`} />}

          <Row label="返却の期限" value={rule.deadline} strong />
          <div style={{ display: 'flex', gap: 8 }}>
            <span style={{ color: BLUE.sub, minWidth: 72 }}>過ぎたら</span>
            <ul data-testid="cruise-over-rules" style={{ margin: 0, paddingLeft: 16, fontSize: '0.8rem' }}>
              {rule.overLines.map((l) => <li key={l}>{l}</li>)}
            </ul>
          </div>

          <Row
            label="事前決済"
            value={c.prepayRequired || c.fromCommentOnly
              ? `必要（請求書でお支払い。貸出前日までに未払いなら連絡）・支払い確認 ${data.payment ? '済' : '未'}`
              : '—'}
            warn={(c.prepayRequired || c.fromCommentOnly) && !data.payment}
          />

          <label
            style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, fontWeight: 600, cursor: canEdit ? 'pointer' : 'default' }}
          >
            <input
              type="checkbox"
              data-testid="cruise-handover-guided"
              checked={c.handoverGuided}
              disabled={!canEdit}
              onChange={(e) => toggleGuided(e.target.checked)}
            />
            お渡し場所の案内が済んだ
          </label>
          {canEdit && <div style={{ fontSize: '0.72rem', color: '#64748b' }}>チェックの変更は、パネル右上の保存で確定します。</div>}
        </div>
      </FieldMarkWrap>
    </>
  );
}

function Row({ label, value, strong, warn }: { label: string; value: string; strong?: boolean; warn?: boolean }) {
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <span style={{ color: BLUE.sub, minWidth: 72 }}>{label}</span>
      <span style={{ fontWeight: strong || warn ? 700 : 500, wordBreak: 'break-all', color: warn ? '#b91c1c' : undefined }}>{value}</span>
    </div>
  );
}
